import {useState} from "react";
import {Link} from "react-router-dom";
import LinkMUI from '@mui/material/Link';
import {consoleLog,toNumber} from '../../core/utilities/Utilities.js';
import {sanitizeId, getLocale, getSalesOrderStatusColor, getSalesOrderDeliveryStatusColor, getStorageOrderStatusColor, getManufacturingOrderStatusColor, computeStockAvailable, computePreSalesAvailable, computeBatchCapacityUsage, getterAssocField, makeGridDateValueGetter} from './ListsUtils.js';
import {useTheme,Button,Typography,Stack,Chip,Box,IconButton,Popover,Tooltip} from '@mui/material';
import {useNotifications} from '../components/Notification/NotificationsContext';
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import DownloadIcon from '@mui/icons-material/Download';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import 'dayjs/locale/pt-br';
import Switch from '@mui/material/Switch';
const file='Entities.js';



const NotificationTypeIcon=({type_db})=>{
    const {getNotificationIcon}=useNotifications();
    return getNotificationIcon(type_db);
};
const NotificationTypeText=({type_db,type})=>{
    const {getNotificationColor}=useNotifications();
    return <Typography 
        variant="body2"
        sx={{
            color:getNotificationColor(type_db),
            display:"flex",
            alignItems:"center",
            lineHeight:"inherit"
        }}>{type}</Typography>
};


const makeGridDateFormatter=(application,type="date")=>(value)=>{
    if(!value) return "";
    const locale=application?.globalData?.locale?.toLowerCase()||"en";
    return dayjs(value).locale(locale).format(type==="datetime"?"L LT":"L");
};

const ThemedLink=({to,onClick,children,openNewTab=false})=>{
  const theme=useTheme();
  const style={
    textDecoration:"none",
    color:theme.list_link_color,
    fontWeight:'normal'
  };
  
  return (
    <Link 
        to={to}
        target={openNewTab?"_blank":undefined}
        rel={openNewTab?"noopener noreferrer":undefined}
        onClick={onClick} 
        style={style}>
      {children}
    </Link>
  );
};

const BooleanIcons=(params,invertLogic=false)=>{
    const isTrue=invertLogic?!params.value:params.value;
    if(params.value===true||params.value===false){
        return isTrue
            ?<CheckCircleIcon sx={{color:'success.main'}}/>
            :<CancelIcon sx={{color:'error.main'}}/>;
    }
    return null;
};

//External warehouse flags (stock check, delivery email) don't apply to transfers (stock leaves from the brewery) - backend sends null for them
const TransferNotApplicableIcons=(params,application)=>{
    const isBreweryOrigin=application.globalData.list_of_values_reverse.Stock_Origin?.[params.row?.stock_origin]==='BREWERY';
    if(isBreweryOrigin){
        return <Tooltip title={getLocale(application,'LabelNotApplicableTransfer')}><RemoveCircleOutlineIcon sx={{color:'text.disabled'}}/></Tooltip>;
    }
    return BooleanIcons(params);
};

const BooleanWarning=(params,invertLogic=false)=>{
    const isTrue=invertLogic?!params.value:params.value;
    if(params.value===true||params.value===false){
        return isTrue
            ?<ReportGmailerrorredIcon sx={{color:'error.main'}}/>
            :null;
    }
    return null;
};

const withEditableIcon=(col)=>{
  if(!col.editable) return col;
  return {
    ...col,
    renderHeader:(params)=>(
      <span style={{display:"flex",alignItems:"center",gap:6}}>
        {params.colDef.headerName}
        <EditIcon fontSize="inherit" style={{opacity:0.35}}/>
      </span>
    )
  };
};

function CountBadge({ count }) {
  return (
    <Box
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.75,
        borderRadius: 999,
        border: "1px solid",
        borderColor: "divider",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.72rem",
        lineHeight: 1,
        color: "text.primary",
        flexShrink: 0,
      }}
    >
      {count}
    </Box>
  );
}

// Free-text notes/description cell, modeled on Puuninki's renderNotesField: the text wraps
// (clamped to 3 lines with an ellipsis, since grid rows are fixed height) and a small icon
// opens a popover with the full text - so long notes can be read straight from the list
// without drilling down. Unlike Puuninki's, the icon opens a self-contained popover rather
// than the row's edit popup, so it works in every list (incl. ones with no popupComponent).
function NotesCell({ text, application }) {
    const [anchorEl, setAnchorEl] = useState(null);
    if (!text) return null;
    return (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 1, width: "100%" }}>
            <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0.25, mt: 0.5, flexShrink: 0 }}
                aria-label={getLocale(application, 'FieldDescription')}>
                <OpenInFullIcon fontSize="inherit" />
            </IconButton>
            <Box
                sx={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    flexGrow: 1,
                    lineHeight: 1.4,
                    fontSize: "smaller",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}>
                {text}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                <Box sx={{ p: 1, width: 400, maxHeight: 300, overflow: "auto" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Box sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{getLocale(application, 'FieldDescription')}</Box>
                        <IconButton size="small" onClick={() => setAnchorEl(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.85rem", lineHeight: 1.5 }}>{text}</Box>
                </Box>
            </Popover>
        </Box>
    );
}

function AssocChipsCell({ params, entity, linkBase = "/product", linkSuffix = "/skus", maxVisible = 2,application ,handleLinkClick }) {
    const items = params?.row?.[entity] ?? [];
    const [anchorEl, setAnchorEl] = useState(null);
    const theme=useTheme();
    const primaryColor=theme.palette.primary.main;

    const open = Boolean(anchorEl);
    const visible = items.slice(0, maxVisible);

    const openPopover = (e) => setAnchorEl(e.currentTarget);
    const closePopover = () => setAnchorEl(null);

    if (!items.length) return null;

    return (
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
            <CountBadge count={items.length} />
            <IconButton
                size="small"
                onClick={openPopover}
                sx={{ p: 0.25, flexShrink: 0 }}
                aria-label={`Show all ${items.length}`}>
                <OpenInFullIcon fontSize="inherit" />
            </IconButton>
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
                whiteSpace: "nowrap",
                flex: 1,
                }}>
        {visible.map((item) => (
          <Chip
            key={item.id}
            size="small"
            style={{fontSize:'0.7125rem',color:'white',backgroundColor:primaryColor}}
            label={
              <Link
                target="_blank"
                rel="noopener noreferrer"
                to={`${linkBase}/${item.id}${linkSuffix}`}
                onClick={handleLinkClick}
                style={{ textDecoration: "none", color: "inherit",fontSize:'0.7125rem' }}
                
              >
                {item.name}
              </Link>
            }
            sx={{
              fontSize: "0.72rem",
              height: 22,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ))}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box sx={{ p: 1, width: 360, maxHeight: 300, overflow: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
              {items.length} {application.getLocale("PageTabItems")}
            </Box>
            <IconButton size="small" onClick={closePopover}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {items.map((item) => (
              <Chip
                key={item.id}
                size="small"
                clickable
                component={Link}
                to={`${linkBase}/${item.id}${linkSuffix}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                label={item.name}
                style={{fontSize:'0.7125rem',color:'white',backgroundColor:primaryColor}}
              />
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

const renderAuditBooleanToSwitch=(params)=>{
    const value=params.value;
    const isBoolean=params.row.is_boolean;
    if(isBoolean){return renderBooleanToSwitch(value);}
    if(value===null||value===undefined) {return <span></span>;}
    return <span>{String(value)}</span>;
}

const renderBooleanToSwitch=(value)=>{
    return <Switch checked={!!value} disabled size="small" />;
}

const renderAccountStatus=(params,application)=>{
    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Account_Status[params.row.status];
    let color="inherit";
    if(STATUS==="ACTIVE") color="darkgreen";
    else if(STATUS==="INACTIVE") color="darkred";
    else if(STATUS==="LOGISTICS_INTEGRATION_ERROR"||STATUS==="INVOICING_INTEGRATION_ERROR") color="red";
    return (
        <Box
            sx={{
                width:'100%',
                height:'100%',
                display:'flex',
                alignItems:'center',
                pl:1,
                color,
            }}>
            {params.row.status}
        </Box>
    );
}

const renderProductStatus=(params,application)=>{
    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Product_Status[params.row.status];
    let color="inherit";
    if(STATUS==="ACTIVE") color="darkgreen";
    else if(STATUS==="INACTIVE") color="darkred";
    else if(STATUS==="LOGISTICS_INTEGRATION_ERROR") color="red";
    return (
        <Box
            sx={{
                width:'100%',
                height:'100%',
                display:'flex',
                alignItems:'center',
                pl:1,
                color,
            }}>
            {params.row.status}
        </Box>
    );
}

export const handleLinkClick=(event)=>{
    const link=event.currentTarget.getAttribute("href");
    consoleLog({file:file,context:'handleLinkClick',arg_name:'link',arg_value:link});
};

const renderSalesOrderStatusCell=(application,fieldName,draftReasonField=null)=>(params)=>{
    const rawStatus=params?.row?.[fieldName];
    const translatedStatus=rawStatus&&application?.globalData?.list_of_values_reverse?.Sales_Order_Status?.[rawStatus];
    const color=getSalesOrderStatusColor(translatedStatus);
    const draftReason=draftReasonField&&params?.row?.[draftReasonField];
    const statusBox=(
      <Box
        sx={{
          width:'100%',
          height:'100%',
          display:'flex',
          alignItems:'center',
          pl:1,
          color,
        }}>
        {rawStatus}
      </Box>
    );
    if(translatedStatus==='DRAFT'&&draftReason){
        return (
            <Tooltip title={draftReason}>
                {statusBox}
            </Tooltip>
        );
    }
    return statusBox;
};

const renderSalesOrderDeliveryStatusCell=(application,fieldName)=>(params)=>{
    const rawStatus=params?.row?.[fieldName];
    const translatedStatus=rawStatus&&application?.globalData?.list_of_values_reverse?.Delivery_Status?.[rawStatus];
    const color=getSalesOrderDeliveryStatusColor(translatedStatus);
    return (
      <Box
        sx={{
          width:'100%',
          height:'100%',
          display:'flex',
          alignItems:'center',
          pl:1,
          color,
        }}>
        {rawStatus}
      </Box>
    );
};

const renderStorageOrderStatusCell=(application,fieldName)=>(params)=>{
    const rawStatus=params?.row?.[fieldName];
    const translatedStatus=rawStatus&&application?.globalData?.list_of_values_reverse?.Storage_Order_Status?.[rawStatus];
    const color=getStorageOrderStatusColor(translatedStatus);
    return (
      <Box
        sx={{
          width:'100%',
          height:'100%',
          display:'flex',
          alignItems:'center',
          pl:1,
          color,
        }}>
        {rawStatus}
      </Box>
    );
};

const renderStorageOrderDeliveryStatusCell=(application,fieldName)=>(params)=>{
    return renderSalesOrderDeliveryStatusCell(application,fieldName)(params);
};

const renderManufacturingOrderStatusCell=(application,fieldName)=>(params)=>{
    const rawStatus=params?.row?.[fieldName];
    const translatedStatus=rawStatus&&application?.globalData?.list_of_values_reverse?.Manufacturing_Order_Status?.[rawStatus];
    const color=getManufacturingOrderStatusColor(translatedStatus);
    return (
      <Box
        sx={{
          width:'100%',
          height:'100%',
          display:'flex',
          alignItems:'center',
          pl:1,
          color,
        }}>
        {rawStatus}
      </Box>
    );
};

// Custom renderCell replaces the DataGrid's own cell rendering, which is what
// right-aligns type:'number' columns by default - so any number column using
// renderCell needs to redo that alignment itself. Use this helper instead of a
// one-off <Box> to keep every custom-rendered number column aligned the same
// way as plain ones, and to always show 0 rather than an empty cell.
const renderNumberCell=(value,extraSx={})=>(
    <Box
        sx={{
            width:'100%',
            height:'100%',
            display:'flex',
            alignItems:'center',
            justifyContent:'flex-end',
            pr:1,
            ...extraSx,
        }}>
        {toNumber(value)}
    </Box>
);

export const columns={
    accounts:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/account/'+sanitizeId(params.row.id)+'/sales_orders'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'legal_name',headerName:getLocale(application,'FieldLegalName'),width:list_properties?.legal_name?.width??200,editable:false,},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderAccountStatus(params,application),
            },
            {field:'business_id',headerName:getLocale(application,'FieldBusinessId'),width:list_properties?.business_id?.width??110,editable:false,}
        ]);
    },
    accounts_find_similar_popup:function(list_properties,application){
        return([
            {field:'similarity_score_percent',headerName:getLocale(application,'FieldSimilarityScorePerCent'),width:list_properties?.similarity_score_percent?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/account/'+sanitizeId(params.row.id)+'/sales_orders'} openNewTab onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderAccountStatus(params,application),
            },
            {field:'business_id',headerName:getLocale(application,'FieldBusinessId'),width:list_properties?.business_id?.width??110,editable:false,},
            {field:'legal_name',headerName:getLocale(application,'FieldLegalName'),width:list_properties?.legal_name?.width??200,editable:false,},
        ]);
    },
    account_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'legal_name',headerName:getLocale(application,'FieldLegalName'),width:list_properties?.legal_name?.width??200,editable:false,},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderAccountStatus(params,application),
            },
            {field:'business_id',headerName:getLocale(application,'FieldBusinessId'),width:list_properties?.business_id?.width??110,editable:false,},
        ]);
    },
    tanks:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/tank/'+sanitizeId(params.row.id)+'/batches'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'capacity',headerName:getLocale(application,'FieldCapacityLiters'),width:list_properties?.capacity?.width??150,editable:false},
            {field:'sorting_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.capacity?.width??150,editable:false},
            {field:'exclude_from_brewday_count',headerName:getLocale(application,'FieldExcludeFromBrewdayCount'),width:list_properties?.exclude_from_brewday_count?.width??200,editable:false,type:'boolean'},
        ]);
    },
    tank_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'capacity',headerName:getLocale(application,'FieldCapacityLiters'),width:list_properties?.capacity?.width??150,editable:false},
            {field:'sorting_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.capacity?.width??150,editable:false},
        ]);
    },
    week_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            // "name" is week_label ("#44-26") aliased server-side (Week.get_list_formatted)
            // so the FieldTextJoinAutocomplete field this popup feeds can use it directly.
            {field:'name',headerName:getLocale(application,'FieldWeekLabel'),width:list_properties?.name?.width??150,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{week_label:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'week_start',headerName:getLocale(application,'FieldWeekStart'),width:list_properties?.week_start?.width??130,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'week_end',headerName:getLocale(application,'FieldWeekEnd'),width:list_properties?.week_end?.width??130,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
        ]);
    },
    tank_batches:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??150,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(params?.row?.can_update_row?<Button name="update" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>:<span>-</span>),},
            {field:'batch_number',headerName:getLocale(application,'FieldNumber'),width:list_properties?.batch_number?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/batch/'+sanitizeId(params.row.id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.batch_number}</ThemedLink>},

            {field:'start_date',headerName:getLocale(application,'FieldStartDate'),width:list_properties?.start_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'end_date',headerName:getLocale(application,'FieldEndDate'),width:list_properties?.end_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},

            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'amount_planned',headerName:getLocale(application,'FieldAmountPLannedLiters'),width:list_properties?.amount_planned?.width??110,editable:false,},
            {field:'total_planned_liters',headerName:getLocale(application,'FieldCapacityUsage'),width:list_properties?.total_planned_liters?.width??160,editable:false,type:'number',
                renderCell:(params)=>{
                    const {plannedLiters,capacityLiters,exceeds}=computeBatchCapacityUsage(params.row);
                    const cell=<Box sx={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'flex-end',pr:1,color:exceeds?'red':'inherit'}}>{plannedLiters} / {capacityLiters} L</Box>;
                    return exceeds
                        ?<Tooltip arrow title={getLocale(application,'LabelCapacityExceeded')}>{cell}</Tooltip>
                        :cell;
                },
                valueGetter:(value,row)=>computeBatchCapacityUsage(row).plannedLiters,
            },
            {field:'product_name',headerName:getLocale(application,'FieldProductName'),width:list_properties?.product_name?.width??150,editable:false,
                renderCell:(params)=><ThemedLink to={'/product/'+sanitizeId(params.row.product_id)+'/skus'} onClick={handleLinkClick}>{params.row.product_name}</ThemedLink>},
            
            {field:'label_design_ready',headerName:getLocale(application,'FieldLabelDesignReady'),width:list_properties?.label_design_ready?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'label_in_house',headerName:getLocale(application,'FieldLabelInHouse'),width:list_properties?.label_in_house?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'recipe_done',headerName:getLocale(application,'FieldRecipeDone'),width:list_properties?.recipe_done?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'ingredients_in_house',headerName:getLocale(application,'FieldIngredientsInHouse'),width:list_properties?.ingredients_in_house?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },

            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
        ]);
    },
    batches:function(list_properties,application){
        return([
            {field:'batch_number',headerName:getLocale(application,'FieldNumber'),width:list_properties?.batch_number?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/batch/'+sanitizeId(params.row.id)+'/manufacturing_orders'} onClick={handleLinkClick}>{params.row.batch_number}</ThemedLink>},
            {field:'tank_name',headerName:getLocale(application,'FieldTankName'),width:list_properties?.tank_name?.width??150,editable:false,
                renderCell:(params)=><ThemedLink to={'/tank/'+sanitizeId(params.row.tank_id)+'/batches'} onClick={handleLinkClick}>{params.row.tank_name}</ThemedLink>},
            {field:'product_name',headerName:getLocale(application,'FieldProductName'),width:list_properties?.product_name?.width??200,editable:false,
                renderCell:(params)=><ThemedLink to={'/product/'+sanitizeId(params.row.product_id)+'/skus'} onClick={handleLinkClick}>{params.row.product_name}</ThemedLink>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'start_date',headerName:getLocale(application,'FieldStartDate'),width:list_properties?.start_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'end_date',headerName:getLocale(application,'FieldEndDate'),width:list_properties?.end_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'amount_planned',headerName:getLocale(application,'FieldAmountPLannedLiters'),width:list_properties?.amount_planned?.width??150,editable:false,},
            {field:'total_planned_liters',headerName:getLocale(application,'FieldCapacityUsage'),width:list_properties?.total_planned_liters?.width??160,editable:false,type:'number',
                renderCell:(params)=>{
                    const {plannedLiters,capacityLiters,exceeds}=computeBatchCapacityUsage(params.row);
                    const cell=<Box sx={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'flex-end',pr:1,color:exceeds?'red':'inherit'}}>{plannedLiters} / {capacityLiters} L</Box>;
                    return exceeds
                        ?<Tooltip arrow title={getLocale(application,'LabelCapacityExceeded')}>{cell}</Tooltip>
                        :cell;
                },
                valueGetter:(value,row)=>computeBatchCapacityUsage(row).plannedLiters,
            },
        ]);
    },
    products:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/product/'+sanitizeId(params.row.id)+'/skus'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderProductStatus(params,application),
            },
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??150,editable:false},
            {field:'gluten_free',headerName:getLocale(application,'FieldGlutenFree'),width:list_properties?.gluten_free?.width??130,editable:false,type:'boolean'},
        ]);
    },
    products_find_similar_popup:function(list_properties,application){
        return([
            {field:'similarity_score_percent',headerName:getLocale(application,'FieldSimilarityScorePerCent'),width:list_properties?.similarity_score_percent?.width??110,editable:false,},
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/product/'+sanitizeId(params.row.id)+'/skus'} openNewTab onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderProductStatus(params,application),
            },
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??150,editable:false},
        ]);
    },
    product_batch_status_popup:function(list_properties,application){//Batch Activate/Inactivate popup - links open in a new tab so the checkbox selection isn't lost
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/product/'+sanitizeId(params.row.id)+'/skus'} openNewTab onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderProductStatus(params,application),
            },
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??150,editable:false},
        ]);
    },
    sales_orders:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sales_order/'+sanitizeId(params.row.id)+'/items'} onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/account/'+sanitizeId(params.row.account_id)+'/sales_orders'} onClick={handleLinkClick}>{params.row.account_name}</ThemedLink>
            },
            {field:'created_at',headerName:getLocale(application,'FieldOrderDate'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'delivery_date',headerName:getLocale(application,'FieldDeliveredDate'),width:list_properties?.delivery_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderSalesOrderStatusCell(application,'status','draft_reason'),
            },
            {field:'items_available_on_stock',headerName:getLocale(application,'FieldItemsAvailableOnStock'),width:list_properties?.items_available_on_stock?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>TransferNotApplicableIcons(params,application),
            },
            {field:'delivery_status',headerName:getLocale(application,'FieldDeliveryStatus'),width:list_properties?.delivery_status?.width??150,editable:false,
                renderCell:renderSalesOrderDeliveryStatusCell(application,'delivery_status'),
            },
            {field:'delivery_price',headerName:getLocale(application,'FieldDeliveryPrice'),width:list_properties?.delivery_price?.width??150,editable:false,type:'number'},
            {field:'total_price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.total_price?.width??150,editable:false,type:'number',
                valueGetter:(value,row)=>row.export||!row.tax_free?row.total_price_included_vat:row.total_price,
                valueFormatter:(value)=>value,
            },
            {field:'invoicing_number',headerName:getLocale(application,'FieldInvoiceNumber'),width:list_properties?.invoicing_number?.width??150,editable:false},
            {field:'created_by',headerName:getLocale(application,'FieldSalesPerson'),width:list_properties?.created_by?.width??150,editable:false},
            {field:'email_sent',headerName:getLocale(application,'FieldEmailSent'),width:list_properties?.email_sent?.width??120,editable:false,type:'boolean',
                renderCell:(params)=>TransferNotApplicableIcons(params,application),
            },
            {field:'internal_destination',headerName:getLocale(application,'FieldInternalDestination'),width:list_properties?.internal_destination?.width??150,editable:false},
            {field:'stock_origin',headerName:getLocale(application,'FieldStockOrigin'),width:list_properties?.stock_origin?.width??120,editable:false},
        ]);
    },
    account_sales_orders:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sales_order/'+sanitizeId(params.row.id)+'/items'} onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderSalesOrderStatusCell(application,'status','draft_reason'),
            },
            {field:'delivery_status',headerName:getLocale(application,'FieldDeliveryStatus'),width:list_properties?.delivery_status?.width??150,editable:false,
                renderCell:renderSalesOrderDeliveryStatusCell(application,'delivery_status'),
            },
            {field:'internal_destination',headerName:getLocale(application,'FieldInternalDestination'),width:list_properties?.internal_destination?.width??150,editable:false},
            {field:'price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.price?.width??150,editable:false,
                valueGetter:(value,row)=>row.export||!row.tax_free?row.price_included_vat:row.price,
                valueFormatter:(value)=>value,
            },
            {field:'palpa_value',headerName:getLocale(application,'FieldTotalPalpa'),width:list_properties?.palpa_value?.width??150,editable:false},
            {field:'total_price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.total_price?.width??150,editable:false,
                valueGetter:(value,row)=>row.export||!row.tax_free?row.total_price_included_vat:row.total_price,
                valueFormatter:(value)=>value,
            },
        ]);
    },
    sales_orders_overdue_popup:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sales_order/'+sanitizeId(params.row.id)+'/items'} openNewTab onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderSalesOrderStatusCell(application,'status'),
            },
            {field:'delivery_status',headerName:getLocale(application,'FieldDeliveryStatus'),width:list_properties?.delivery_status?.width??150,editable:false,
                renderCell:renderSalesOrderDeliveryStatusCell(application,'delivery_status'),
            },
            {field:'price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.price?.width??150,editable:false,
                valueGetter:(value,row)=>row.export||!row.tax_free?row.price_included_vat:row.price,
                valueFormatter:(value)=>value,
            },
            {field:'palpa_value',headerName:getLocale(application,'FieldTotalPalpa'),width:list_properties?.palpa_value?.width??150,editable:false},
            {field:'total_price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.total_price?.width??150,editable:false,
                valueGetter:(value,row)=>row.export||!row.tax_free?row.total_price_included_vat:row.total_price,
                valueFormatter:(value)=>value,
            },
        ]);
    },
    sales_order_items:function(list_properties,application,handleLinkClick,entityRules){
        const cols=[
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??100,editable:false,},
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'quantity',headerName:getLocale(application,'FieldQuantityOrdered'),width:list_properties?.quantity?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("quantity"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'unit_price',headerName:getLocale(application,'FieldUnitPrice'),width:list_properties?.unit_price?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("unit_price"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'discount_percent',headerName:getLocale(application,'FieldDiscountPercent'),width:list_properties?.discount_percent?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("discount_percent"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'vat',headerName:getLocale(application,'FieldVATPercent'),width:list_properties?.vat?.width??150,editable:false},
            {field:'vat_price',headerName:getLocale(application,'FieldVATPrice'),width:list_properties?.vat_price?.width??150,editable:false},
            {field:'final_price_included_vat',headerName:getLocale(application,'FieldFinalPriceVATIncluded'),width:list_properties?.final_price_included_vat?.width??150,editable:false},
            {field:'final_price',headerName:getLocale(application,'FieldFinalPrice'),width:list_properties?.final_price?.width??150,editable:false},
            {field:'sku_unit_palpa_value',headerName:getLocale(application,'FieldPalpaSKU'),width:list_properties?.sku_unit_palpa_value?.width??150,editable:false},
            {field:'sku_total_palpa_value',headerName:getLocale(application,'FieldPalpaTotal'),width:list_properties?.sku_total_palpa_value?.width??150,editable:false},
            {field:'quantity_delivered',headerName:getLocale(application,'FieldQuantityDelivered'),width:list_properties?.quantity_delivered?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>value===null||value===undefined?null:toNumber(value)},
            {field:'description',headerName:getLocale(application,'FieldComments'),width:list_properties?.description?.width??110,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("description"),},
        ];
        return cols.map(withEditableIcon);
    },
    sku_sales_order_items:function(list_properties,application,handleLinkClick){        
        return([
            {field:'order_reference',headerName:getLocale(application,'FieldOrderReference'),width:list_properties?.order_reference?.width??100,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sales_order/'+sanitizeId(params.row.sales_order_id)+'/items'} onClick={handleLinkClick}>{params.row.order_reference}</ThemedLink>
            },
            {field:'order_account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.order_account_name?.width??150,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/account/'+sanitizeId(params.row.order_account_id)+'/sales_orders'} onClick={handleLinkClick}>{params.row.order_account_name}</ThemedLink>
            },
            {field:'order_status',headerName:getLocale(application,'FieldOrderStatus'),width:list_properties?.order_status?.width??150,editable:false,
                renderCell:renderSalesOrderStatusCell(application,'order_status'),
            },
            {field:'order_delivery_status',headerName:getLocale(application,'FieldOrderDeliveryStatus'),width:list_properties?.order_delivery_status?.width??150,editable:false,
                renderCell:renderSalesOrderDeliveryStatusCell(application,'order_delivery_status'),
            },
            {field:'quantity',headerName:getLocale(application,'FieldQuantityOrdered'),width:list_properties?.quantity?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
        ]);
    },
    sku_storage_order_items:function(list_properties,application,handleLinkClick){        
        return([
            {field:'order_reference',headerName:getLocale(application,'FieldOrderReference'),width:list_properties?.order_reference?.width??100,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/storage_order/'+sanitizeId(params.row.storage_order_id)+'/items'} onClick={handleLinkClick}>{params.row.order_reference}</ThemedLink>
            },
            {field:'order_status',headerName:getLocale(application,'FieldOrderStatus'),width:list_properties?.order_status?.width??150,editable:false,
                renderCell:renderSalesOrderStatusCell(application,'order_status'),
            },
            {field:'order_delivery_status',headerName:getLocale(application,'FieldOrderDeliveryStatus'),width:list_properties?.order_delivery_status?.width??150,editable:false,
                renderCell:renderStorageOrderDeliveryStatusCell(application,'order_delivery_status'),
            },
            {field:'quantity',headerName:getLocale(application,'FieldSendQty'),width:list_properties?.quantity?.width??110,editable:false,type:"number",align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'quantity_delivered',headerName:getLocale(application,'FieldReceivedQty'),width:list_properties?.quantity_delivered?.width??110,editable:false,type:"number",align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??100,editable:false,},
            {field:'expiration_date',headerName:getLocale(application,'FieldExpirationDate'),width:list_properties?.expiration_date?.width??100,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'production_date',headerName:getLocale(application,'FieldProductionDate'),width:list_properties?.production_date?.width??100,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'description',headerName:getLocale(application,'FieldComments'),width:list_properties?.description?.width??110,editable:false,},
        ]);
    },
    sku_batches:function(list_properties,application){
        return([
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??130,editable:false,sortComparator:(a,b)=>(Number(a)||0)-(Number(b)||0)},
            {field:'total_units',headerName:getLocale(application,'FieldTotalUnitsReceived'),width:list_properties?.total_units?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'time_to_sell',headerName:getLocale(application,'FieldTimeToSell'),width:list_properties?.time_to_sell?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>value!=null?toNumber(value):null},
            {field:'arrival_date',headerName:getLocale(application,'FieldWarehouseArrivalDate'),width:list_properties?.arrival_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'first_sale_date',headerName:getLocale(application,'FieldFirstSaleDate'),width:list_properties?.first_sale_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'last_sale_date',headerName:getLocale(application,'FieldLastSaleDate'),width:list_properties?.last_sale_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'expiration_date',headerName:getLocale(application,'FieldExpirationDate'),width:list_properties?.expiration_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
        ]);
    },
    storage_order_items:function(list_properties,application,handleLinkClick,entityRules){
        const cols=[
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??100,editable:false,},
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'quantity',headerName:getLocale(application,'FieldSendQty'),width:list_properties?.quantity?.width??110,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("quantity"),type:"number",align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'quantity_delivered',headerName:getLocale(application,'FieldReceivedQty'),width:list_properties?.quantity_delivered?.width??110,editable:false,type:"number",align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??100,editable:false,},
            
            {field:'expiration_date',headerName:getLocale(application,'FieldExpirationDate'),width:list_properties?.expiration_date?.width??100,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'production_date',headerName:getLocale(application,'FieldProductionDate'),width:list_properties?.production_date?.width??100,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            
            {field:'description',headerName:getLocale(application,'FieldComments'),width:list_properties?.description?.width??110,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("description"),},
        ];
        return cols.map(withEditableIcon);
    },
    storage_orders:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/storage_order/'+sanitizeId(params.row.id)+'/items'} onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderStorageOrderStatusCell(application,'status'),
            },
            {field:'delivery_status',headerName:getLocale(application,'FieldDeliveryStatus'),width:list_properties?.delivery_status?.width??150,editable:false,
                renderCell:renderStorageOrderDeliveryStatusCell(application,'delivery_status'),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'products',headerName:getLocale(application,'ProductsTitle'),width:list_properties?.products?.width??300,editable:false,
                renderCell:(params)=>(
                    <AssocChipsCell params={params} entity="products" linkBase="/product" linkSuffix="/skus" maxVisible={2} application={application} handleLinkClick={handleLinkClick} />
                ),
                valueGetter:(value,row)=>getterAssocField(value,row,'products'),
            },
        ]);
    },
    skus:function(list_properties,application){
        return([
            {field:'gtin_name',headerName:getLocale(application,'FieldGTINName'),width:list_properties?.gtin_name?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },

             //used to be 'stock_balance'
            {field:'stock_available',headerName:getLocale(application,'FieldStockAvailable'),width:list_properties?.stock_available?.width??100,editable:false,description:getLocale(application,'LabelStockAvailable'),type:'number',
                renderCell:(params)=>{
                    const stock_available=computeStockAvailable(params.row);
                    return renderNumberCell(stock_available,{color:stock_available>0?'blue':stock_available<0?'red':'inherit'});
                },
                valueGetter:(value,row)=>computeStockAvailable(row),
            },
            
            {field:'quarantined_balance',headerName:getLocale(application,'FieldQuarantinedBalance'),width:list_properties?.quarantined_balance?.width??100,editable:false,type:'number',
                renderCell:(params)=>{
                    const highlight=!['',undefined,0,null].includes(params.row.quarantined_balance);
                    return renderNumberCell(params.row.quarantined_balance,{backgroundColor:highlight?'pink':'inherit'});
                },
            },
            {field:'reserved_quantity',headerName:getLocale(application,'FieldReservedQuantity'),width:list_properties?.reserved_quantity?.width??130,editable:false,description:getLocale(application,'LabelTotalReservedCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'total_ordered_amount',headerName:getLocale(application,'FieldTotalOrderedAmount'),width:list_properties?.total_ordered_amount?.width??156,editable:false,description:getLocale(application,'LabelTotalOrderedAmount'),type:'number',valueGetter:(value)=>toNumber(value)},
            
            //used to be 'stock_available'
            {field:'stock_balance',headerName:getLocale(application,'FieldStockBalanceShort'),width:list_properties?.stock_balance?.width??140,editable:false,description:getLocale(application,'LabelTotalExistingCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'brewery_stock_balance',headerName:getLocale(application,'FieldBreweryStockBalance'),width:list_properties?.brewery_stock_balance?.width??140,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'in_transit_quantity',headerName:getLocale(application,'FieldInTransitQuantity'),description:getLocale(application,'LabelInTransitQuantity'),width:list_properties?.in_transit_quantity?.width??140,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'transfer_reserved_quantity',headerName:getLocale(application,'FieldTransferReservedQuantity'),description:getLocale(application,'LabelTransferReservedQuantity'),width:list_properties?.transfer_reserved_quantity?.width??140,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},

            {field:'pre_sales_quantity',headerName:getLocale(application,'FieldPreSalesQuantity'),width:list_properties?.pre_sales_quantity?.width??130,editable:false,description:getLocale(application,'LabelPreSalesQuantity'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'pre_sales_available',headerName:getLocale(application,'FieldPreSalesAvailable'),description:getLocale(application,'LabelPreSalesAvailable'),width:list_properties?.pre_sales_available?.width??140,editable:false,type:'number',
                renderCell:(params)=>{
                    const pre_sales_available=computePreSalesAvailable(params.row);
                    return renderNumberCell(pre_sales_available,{color:pre_sales_available>0?'blue':pre_sales_available<0?'red':'inherit'});
                },

                valueGetter:(value,row)=>computePreSalesAvailable(row)
            },
            {field:'earliest_batch_expiry',headerName:getLocale(application,'FieldExpiryDate'),description:getLocale(application,'LabelEarliestBatchExpiry'),width:list_properties?.earliest_batch_expiry?.width??140,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
                renderCell:(params)=>{
                    const val=params.row.earliest_batch_expiry;
                    if(!val) return null;
                    const expiry=new Date(val);
                    const today=new Date();
                    const twoMonths=new Date();twoMonths.setMonth(twoMonths.getMonth()+2);
                    const color=expiry<today?'red':expiry<twoMonths?'brown':'inherit';
                    return (
                        <Box sx={{width:'100%',height:'100%',display:'flex',alignItems:'center',pl:1,color}}>
                            {params.formattedValue}
                        </Box>
                    );
                },
            },
            //{field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??90,editable:false},
            {field:'pack_type',headerName:getLocale(application,'FieldPackageType'),width:list_properties?.pack_type?.width??105,editable:false},
            {field:'product_type',headerName:getLocale(application,'FieldProductType'),width:list_properties?.product_type?.width??100,editable:false},
            {field:'product_category',headerName:getLocale(application,'FieldProductCategory'),width:list_properties?.product_category?.width??130,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??147,editable:false,
                renderCell:(params)=>{
                    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Product_Status[params.row.status];
                    let color="inherit";
                    if(STATUS==="ACTIVE") color="darkgreen";
                    else if(STATUS==="INACTIVE") color="darkred";
                    else if(STATUS==="LOGISTICS_INTEGRATION_ERROR") color="red";
                    return (
                        <Box
                            sx={{
                                width:'100%',
                                height:'100%',
                                display:'flex',
                                alignItems:'center',
                                color,
                                pl:1,
                            }}>
                            {params.row.status}
                        </Box>
                    );
                },
            },
            {field:'sellable',headerName:getLocale(application,'FieldSellable'),width:list_properties?.sellable?.width??100,editable:false,type:'boolean'},
        ]);
    },
    product_skus: function(list_properties,application){
        return([
            {field:'gtin_name',headerName:getLocale(application,'FieldGTINName'),width:list_properties?.gtin_name?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },

            //used to be 'stock_balance'
            {field:'stock_available',headerName:getLocale(application,'FieldStockAvailable'),width:list_properties?.stock_available?.width??80,editable:false,description:getLocale(application,'LabelStockAvailable'),type:'number',
                renderCell:(params)=>{
                    const stock_available=computeStockAvailable(params.row);
                    return renderNumberCell(stock_available,{color:stock_available>0?'blue':stock_available<0?'red':'inherit'});
                },
                valueGetter:(value,row)=>computeStockAvailable(row),
            },

            {field:'quarantined_balance',headerName:getLocale(application,'FieldQuarantinedBalance'),width:list_properties?.quarantined_balance?.width??100,editable:false,type:'number',
                renderCell:(params)=>{
                    const highlight=!['',undefined,0,null].includes(params.row.quarantined_balance);
                    return renderNumberCell(params.row.quarantined_balance,{backgroundColor:highlight?'pink':'inherit'});
                },
            },
            
            {field:'reserved_quantity',headerName:getLocale(application,'FieldReservedQuantity'),width:list_properties?.reserved_quantity?.width??150,editable:false,description:getLocale(application,'LabelTotalReservedCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'total_ordered_amount',headerName:getLocale(application,'FieldTotalOrderedAmount'),width:list_properties?.total_ordered_amount?.width??80,editable:false,description:getLocale(application,'LabelTotalOrderedAmount'),type:'number',valueGetter:(value)=>toNumber(value)},
            
            //used to be 'stock_available'
            {field:'stock_balance',headerName:getLocale(application,'FieldStockBalanceShort'),width:list_properties?.stock_balance?.width??150,editable:false,description:getLocale(application,'LabelTotalExistingCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'brewery_stock_balance',headerName:getLocale(application,'FieldBreweryStockBalance'),width:list_properties?.brewery_stock_balance?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'in_transit_quantity',headerName:getLocale(application,'FieldInTransitQuantity'),description:getLocale(application,'LabelInTransitQuantity'),width:list_properties?.in_transit_quantity?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'transfer_reserved_quantity',headerName:getLocale(application,'FieldTransferReservedQuantity'),description:getLocale(application,'LabelTransferReservedQuantity'),width:list_properties?.transfer_reserved_quantity?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},

            {field:'pre_sales_quantity',headerName:getLocale(application,'FieldPreSalesQuantity'),width:list_properties?.pre_sales_quantity?.width??150,editable:false,description:getLocale(application,'LabelPreSalesQuantity'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'pre_sales_available',headerName:getLocale(application,'FieldPreSalesAvailable'),description:getLocale(application,'LabelPreSalesAvailable'),width:list_properties?.pre_sales_available?.width??80,editable:false,type:'number',
                renderCell:(params)=>{
                    const pre_sales_available=computePreSalesAvailable(params.row);
                    return renderNumberCell(pre_sales_available,{color:pre_sales_available>0?'blue':pre_sales_available<0?'red':'inherit'});
                },
                
                valueGetter:(value,row)=>computePreSalesAvailable(row)
            },
            /*
            {field:'quarantined_balance',headerName:getLocale(application,'FieldQuarantinedBalance'),width:list_properties?.quarantined_balance?.width??150,editable:false,type:'number',
                renderCell:(params)=>{
                    const highlight=!['',undefined,0,null].includes(params.row.quarantined_balance);
                    return renderNumberCell(params.row.quarantined_balance,{backgroundColor:highlight?'pink':'inherit'});
                },
            },
            */
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'pack_type',headerName:getLocale(application,'FieldPackageType'),width:list_properties?.pack_type?.width??150,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>{
                    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Product_Status[params.row.status];
                    let color="inherit";
                    if(STATUS==="ACTIVE") color="darkgreen";
                    else if(STATUS==="INACTIVE") color="darkred";
                    else if(STATUS==="LOGISTICS_INTEGRATION_ERROR") color="red";
                    return (
                        <Box
                            sx={{
                                width:'100%',
                                height:'100%',
                                display:'flex',
                                alignItems:'center',
                                color,
                                pl:1,
                            }}>
                            {params.row.status}
                        </Box>
                    );
                },
            },
        ]);
    },
    sku_multipacks:function(list_properties,application,handleClickEvent){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,},
            {field:'gtin_name',headerName:getLocale(application,'FieldGTINName'),width:list_properties?.gtin_name?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>{
                    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Product_Status[params.row.status];
                    let color="inherit";
                    if(STATUS==="ACTIVE") color="darkgreen";
                    else if(STATUS==="INACTIVE") color="darkred";
                    else if(STATUS==="LOGISTICS_INTEGRATION_ERROR") color="red";
                    return (
                        <Box
                            sx={{
                                width:'100%',
                                height:'100%',
                                display:'flex',
                                alignItems:'center',
                                color,
                                pl:1,
                            }}>
                            {params.row.status}
                        </Box>
                    );
                },
            },
        ])
    },
    sku_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'gtin_name',headerName:getLocale(application,'FieldGTINName'),width:list_properties?.gtin_name?.width??110,editable:false,},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            
            //used to be 'stock_balance'
            {field:'stock_available',headerName:getLocale(application,'FieldStockAvailable'),width:list_properties?.stock_available?.width??80,editable:false,description:getLocale(application,'LabelStockAvailable'),type:'number',
                renderCell:(params)=>{
                    const stock_available=computeStockAvailable(params.row);
                    return renderNumberCell(stock_available,{color:stock_available>0?'blue':stock_available<0?'red':'inherit'});
                },
                valueGetter:(value,row)=>computeStockAvailable(row),
            },

            {field:'quarantined_balance',headerName:getLocale(application,'FieldQuarantinedBalance'),width:list_properties?.quarantined_balance?.width??100,editable:false,type:'number',
                renderCell:(params)=>{
                    const highlight=!['',undefined,0,null].includes(params.row.quarantined_balance);
                    return renderNumberCell(params.row.quarantined_balance,{backgroundColor:highlight?'pink':'inherit'});
                },
            },

            {field:'reserved_quantity',headerName:getLocale(application,'FieldReservedQuantity'),width:list_properties?.reserved_quantity?.width??150,editable:false,description:getLocale(application,'LabelTotalReservedCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'total_ordered_amount',headerName:getLocale(application,'FieldTotalOrderedAmount'),width:list_properties?.total_ordered_amount?.width??80,editable:false,description:getLocale(application,'LabelTotalOrderedAmount'),type:'number',valueGetter:(value)=>toNumber(value)},
            
            //used to be 'stock_balance'
            {field:'stock_balance',headerName:getLocale(application,'FieldStockBalanceShort'),width:list_properties?.stock_balance?.width??150,editable:false,description:getLocale(application,'LabelTotalExistingCollico'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'brewery_stock_balance',headerName:getLocale(application,'FieldBreweryStockBalance'),width:list_properties?.brewery_stock_balance?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'in_transit_quantity',headerName:getLocale(application,'FieldInTransitQuantity'),description:getLocale(application,'LabelInTransitQuantity'),width:list_properties?.in_transit_quantity?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'transfer_reserved_quantity',headerName:getLocale(application,'FieldTransferReservedQuantity'),description:getLocale(application,'LabelTransferReservedQuantity'),width:list_properties?.transfer_reserved_quantity?.width??150,editable:false,type:'number',valueGetter:(value)=>toNumber(value)},

            {field:'pre_sales_quantity',headerName:getLocale(application,'FieldPreSalesQuantity'),width:list_properties?.pre_sales_quantity?.width??150,editable:false,description:getLocale(application,'LabelPreSalesQuantity'),type:'number',valueGetter:(value)=>toNumber(value)},
            {field:'pre_sales_available',headerName:getLocale(application,'FieldPreSalesAvailable'),description:getLocale(application,'LabelPreSalesAvailable'),width:list_properties?.pre_sales_available?.width??80,editable:false,type:'number',
                renderCell:(params)=>{
                    const pre_sales_available=computePreSalesAvailable(params.row);
                    return renderNumberCell(pre_sales_available,{color:pre_sales_available>0?'blue':pre_sales_available<0?'red':'inherit'});
                },

                valueGetter:(value,row)=>computePreSalesAvailable(row)
            },
        ])
    },
    gtins:function(list_properties,application,handleClickEvent,entityRules){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,},
            {field:'blocked',headerName:getLocale(application,'FieldGTINBlocked'),width:list_properties?.blocked?.width??110,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("blocked"),type:'boolean'},
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/sales_order_items'} onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'sku_status',headerName:getLocale(application,'FieldSKUStatus'),width:list_properties?.sku_status?.width??150,editable:false,},
            {field:'comments',headerName:getLocale(application,'FieldComments'),width:list_properties?.comments?.width??110,editable:false,},
        ]);
    },
    gtin_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'comments',headerName:getLocale(application,'FieldComments'),width:list_properties?.comments?.width??110,editable:false,},
        ]);
    },
    product_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.capacity?.width??150,editable:false},
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??150,editable:false},
        ]);
    },
    audit:function(list_properties,application){
        return([
            {field:'entity_column',headerName:getLocale(application,'AuditField'),width:list_properties?.entity_column?.width??300,editable:false},
            {field:'value_old',headerName:getLocale(application,'AuditOldValue'),width:list_properties?.value_old?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'value_new',headerName:getLocale(application,'AuditNewValue'),width:list_properties?.value_new?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'created_at',headerName:getLocale(application,'AuditDateTime'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'AuditUser'),width:list_properties?.created_by?.width??300,editable:false},
        ]);
    },
    email_log:function(list_properties,application){
        return([
            {field:'email_type',headerName:getLocale(application,'FieldEmailLogEmailType'),width:list_properties?.email_type?.width??200,editable:false},
            {field:'recipient_email',headerName:getLocale(application,'FieldEmailLogRecipient'),width:list_properties?.recipient_email?.width??250,editable:false},
            {field:'status',headerName:getLocale(application,'FieldEmailLogStatus'),width:list_properties?.status?.width??120,editable:false},
            {field:'trigger',headerName:getLocale(application,'FieldEmailLogTrigger'),width:list_properties?.trigger?.width??120,editable:false},
            {field:'error_message',headerName:getLocale(application,'FieldEmailLogErrorMessage'),width:list_properties?.error_message?.width??300,editable:false},
            {field:'created_at',headerName:getLocale(application,'AuditDateTime'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'AuditUser'),width:list_properties?.created_by?.width??200,editable:false},
        ]);
    },
    users:function(list_properties,application){
        return([
            {field:'login',headerName:getLocale(application,'FieldLogin'),width:list_properties?.login?.width??150,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/user/'+sanitizeId(params.row.id)+'/audit'} onClick={handleLinkClick}>{params.row.login}</ThemedLink>},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false},
            {field:'phone',headerName:getLocale(application,'FieldPhone'),width:list_properties?.phone?.width??150,editable:false},
        ]);
    },
    list_of_values:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("code")},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("name")},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("language")},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("value")},
            {field:'sorting_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sorting_order?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("sorting_order"),type:'number',valueGetter:(value)=>toNumber(value)},
        ];
        return cols.map(withEditableIcon);
    },
    locales:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("code")},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("language")},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("value")},
        ];
        return cols.map(withEditableIcon);
    },
    notifications:function(list_properties,application){
        return([
            {field:'system_user_login',headerName:getLocale(application,'FooterUser'),width:list_properties?.system_user_login?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/user/'+sanitizeId(params.row.system_user_id)+'/audit'} onClick={handleLinkClick}>{params.row.system_user_login}</ThemedLink>
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/notification/'+sanitizeId(params.row.id)} onClick={handleLinkClick}>{params.row.title}</ThemedLink>
            },
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??250,editable:false},
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??250,editable:false},
            {field:'link',headerName:getLocale(application,'FieldLink'),width:list_properties?.link?.width??250,editable:false},
            {field:'table_name',headerName:getLocale(application,'FieldTableName'),width:list_properties?.table_name?.width??250,editable:false},
            {field:'record_id',headerName:getLocale(application,'FieldId'),width:list_properties?.record_id?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    my_notifications:function(list_properties,application,handleClickEvent){
        return([
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??180,editable:false,
                renderCell:(params)=>(
                    <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                        <NotificationTypeIcon type_db={params.row.type_db} />
                        <NotificationTypeText type_db={params.row.type_db} type={params.row.type}/>
                    </Box>
                )
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink 
                        onClick={(e)=>{
                            e.stopPropagation();
                            e.preventDefault();
                            handleClickEvent({action:'invoke_method',args:{method:"myNotificationDrilldown",recordId:params.row.id,read:params.row.read}})
                        }
                    }>
                        {params.row.title}
                    </ThemedLink>
            },

            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    tags:function(list_properties,application,handleClickEvent,entityRules){
       const cols=[
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("value")},
            {field:'products',headerName:getLocale(application,'ProductsTitle'),width:list_properties?.products?.width??300,editable:false,
                renderCell:(params)=>(
                    <AssocChipsCell
                        params={params}
                        entity="products"
                        linkBase="/product"
                        linkSuffix="/skus"
                        maxVisible={2}
                        application={application}
                        handleLinkClick={handleLinkClick}
                    />
                ),
                valueGetter:(value,row)=>getterAssocField(value,row,'products'),
            },
            {field:'skus',headerName:getLocale(application,'SKUsTitle'),width:list_properties?.skus?.width??300,editable:false,
                renderCell:(params)=>(
                    <AssocChipsCell
                        params={params}
                        entity="skus"
                        linkBase="/sku"
                        linkSuffix="/sales_order_items"
                        maxVisible={2}
                        application={application}
                        handleLinkClick={handleLinkClick}
                    />
                ),
                valueGetter:(value,row)=>getterAssocField(value,row,'products'),
            },
        ];
        return cols.map(withEditableIcon);
    },
    error_logs:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??350,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(
                <Stack direction="row" spacing={1}>
                    <Button name="goToRecord" variant="contained" onClick={(e)=>handleClickEvent({action:'invoke_method',args:{method:"goToRecord",recordId:params.row.record_id,tableName:params.row.table_name}})}>{getLocale(application,'ButtonGoToTableRecord')}</Button>
                    <Button name="viewDetail" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonViewDetail')}</Button>
                </Stack>
            ),},
            {field:'record_id',headerName:getLocale(application,'FieldId'),width:list_properties?.record_id?.width??300,editable:false},
            {field:'table_name',headerName:getLocale(application,'FieldTableName'),width:list_properties?.table_name?.width??110,editable:false},
            {field:'source',headerName:getLocale(application,'FieldSource'),width:list_properties?.source?.width??250,editable:false},
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            //{field:'integration_request_data',headerName:getLocale(application,'FieldIntegrationRequestData'),width:list_properties?.integration_request_data?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
        ]);
    },
    error_logs_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??150,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(
                    <Button name="viewDetail" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonViewDetail')}</Button>
            ),},
            {field:'source',headerName:getLocale(application,'FieldSource'),width:list_properties?.source?.width??250,editable:false},
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            //{field:'integration_request_data',headerName:getLocale(application,'FieldIntegrationRequestData'),width:list_properties?.integration_request_data?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
        ]);
    },
    alcohol_licenses:function(list_properties,application,handleClickEvent){
        return([
            {field:'license_id',headerName:getLocale(application,'FieldExciseNumber'),width:list_properties?.license_id?.width??250,editable:false},
            {field:'business_name',headerName:getLocale(application,'FieldBusinessName'),width:list_properties?.business_name?.width??250,editable:false},
            {field:'business_legal_name',headerName:getLocale(application,'FieldBusinessLegalName'),width:list_properties?.business_legal_name?.width??150,editable:false},
            {field:'business_addr_city',headerName:getLocale(application,'FieldCity'),width:list_properties?.business_addr_city?.width??150,editable:false},
            {field:'business_addr_street',headerName:getLocale(application,'FieldStreet'),width:list_properties?.business_addr_street?.width??250,editable:false},
            {field:'business_addr_zip',headerName:getLocale(application,'FieldZipCode'),width:list_properties?.business_addr_zip?.width??250,editable:false},
            {field:'license_type',headerName:getLocale(application,'FieldExciseType'),width:list_properties?.license_type?.width??150,editable:false},
            {field:'validity_starting_date',headerName:getLocale(application,'FieldValidityStart'),width:list_properties?.validity_starting_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'validity_end_date',headerName:getLocale(application,'FieldValidityEnd'),width:list_properties?.validity_end_date?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            }
        ]);
    },
    alcohol_licenses_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'license_id',headerName:getLocale(application,'FieldExciseNumber'),width:list_properties?.license_id?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.license_id})}>{params.row.license_id}</LinkMUI>},
            {field:'business_name',headerName:getLocale(application,'FieldBusinessName'),width:list_properties?.business_name?.width??250,editable:false},
            {field:'business_legal_name',headerName:getLocale(application,'FieldBusinessLegalName'),width:list_properties?.business_legal_name?.width??150,editable:false},
            {field:'business_addr_city',headerName:getLocale(application,'FieldCity'),width:list_properties?.business_addr_city?.width??150,editable:false},
            {field:'business_addr_street',headerName:getLocale(application,'FieldStreet'),width:list_properties?.business_addr_street?.width??250,editable:false},
            {field:'business_addr_zip',headerName:getLocale(application,'FieldZipCode'),width:list_properties?.business_addr_zip?.width??250,editable:false},
            {field:'license_type',headerName:getLocale(application,'FieldExciseType'),width:list_properties?.license_type?.width??150,editable:false},
            {field:'validity_starting_date_std',headerName:getLocale(application,'FieldValidityStart'),width:list_properties?.validity_starting_date_std?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'validity_end_date_std',headerName:getLocale(application,'FieldValidityEnd'),width:list_properties?.validity_end_date_std?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            }
        ]);
    },
    alcohol_licenses_popup_sales_order:function(list_properties,application,handleClickEvent){
        return([
            {field:'license_id',headerName:getLocale(application,'FieldExciseNumber'),width:list_properties?.license_id?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.license_id})}>{params.row.license_id}</LinkMUI>},
            {field:'business_name',headerName:getLocale(application,'FieldBusinessName'),width:list_properties?.business_name?.width??250,editable:false},
            {field:'business_legal_name',headerName:getLocale(application,'FieldBusinessLegalName'),width:list_properties?.business_legal_name?.width??150,editable:false},
            {field:'business_addr_city',headerName:getLocale(application,'FieldCity'),width:list_properties?.business_addr_city?.width??150,editable:false},
            {field:'business_addr_street',headerName:getLocale(application,'FieldStreet'),width:list_properties?.business_addr_street?.width??250,editable:false},
            {field:'business_addr_zip',headerName:getLocale(application,'FieldZipCode'),width:list_properties?.business_addr_zip?.width??250,editable:false},
            {field:'license_type',headerName:getLocale(application,'FieldExciseType'),width:list_properties?.license_type?.width??150,editable:false},
            {field:'validity_starting_date_std',headerName:getLocale(application,'FieldValidityStart'),width:list_properties?.validity_starting_date_std?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'validity_end_date_std',headerName:getLocale(application,'FieldValidityEnd'),width:list_properties?.validity_end_date_std?.width??150,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            }
        ]);
    },
    delivery_pricing_rules:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/delivery_pricing_rule/'+sanitizeId(params.row.id)+'/cities'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'priority_order',headerName:getLocale(application,'FieldPriorityOrder'),width:list_properties?.priority_order?.width??250,editable:false},
            {field:'delivery_price',headerName:getLocale(application,'FieldDeliveryPrice'),width:list_properties?.delivery_price?.width??150,editable:false},
            {field:'min_order_value',headerName:getLocale(application,'FieldMinOrderValue'),width:list_properties?.min_order_value?.width??150,editable:false},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??250,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.default?.width??250,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false},
        ]);
    },
    delivery_pricing_rule_cities:function(list_properties,application,handleClickEvent){
        return([
            {field:'city',headerName:getLocale(application,'FieldCity'),width:list_properties?.city?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false},
        ]);
    },
    app_roles:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'role',headerName:getLocale(application,'FieldRole'),width:list_properties?.role?.width??150,editable:false},
            {field:'main_color',headerName:getLocale(application,'FieldMainColor'),width:list_properties?.main_color?.width??120,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("main_color")},
            {field:'list_link_color',headerName:getLocale(application,'FieldListLinkColor'),width:list_properties?.list_link_color?.width??130,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("list_link_color")},
            {field:'landing_page',headerName:getLocale(application,'FieldLandingPage'),width:list_properties?.landing_page?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("landing_page")},
        ];
        return cols.map(withEditableIcon);
    },
    app_configs:function(list_properties,application,handleClickEvent,entityRules){
        const canUpdate=entityRules?.can_update;
        const editable=(field)=>canUpdate&&(entityRules?.can_update_list_fields?.includes(field)??false);
        const cols=[
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??160,editable:editable('category')},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??280,editable:editable('name')},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??200,editable:editable('value'),
                renderCell:(params)=>params.row.is_sensitive
                    ?<span style={{letterSpacing:'2px',color:'var(--color-text-secondary,#888)'}}>{'●●●●●●●●'}</span>
                    :params.value
            },
            {field:'value_type',headerName:getLocale(application,'FieldValueType'),width:list_properties?.value_type?.width??100,editable:editable('value_type')},
            {field:'core',headerName:getLocale(application,'FieldCore'),width:list_properties?.core?.width??80,editable:false,type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'ui_available',headerName:getLocale(application,'FieldUiAvailable'),width:list_properties?.ui_available?.width??100,editable:editable('ui_available'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'is_sensitive',headerName:getLocale(application,'FieldSensitive'),width:list_properties?.is_sensitive?.width??100,editable:editable('is_sensitive'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:editable('description')},
        ];
        return cols.map(withEditableIcon);
    },
    app_menus:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu/'+sanitizeId(params.row.id)+'/items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'menu_label',headerName:getLocale(application,'FieldMenuLabel'),width:list_properties?.menu_label?.width??200,editable:false},
            {field:'menu_color',headerName:getLocale(application,'FieldMenuColor'),width:list_properties?.menu_color?.width??130,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_items:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item/'+sanitizeId(params.row.id)+'/routes'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'icon_name',headerName:getLocale(application,'FieldIconName'),width:list_properties?.icon_name?.width??150,editable:false},
            {field:'visible',headerName:getLocale(application,'FieldVisible'),width:list_properties?.visible?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_routes:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item_route/'+sanitizeId(params.row.id)+'/route_tabs'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??250,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_route_tabs:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false},
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??150,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    package_types:function(list_properties,application,handleClickEvent,entityRules){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("name")},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("type")},
            {field:'unit',headerName:getLocale(application,'FieldUnit'),width:list_properties?.unit?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("unit")},
            {field:'amount',headerName:getLocale(application,'FieldAmount'),width:list_properties?.amount?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("amount"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'width',headerName:getLocale(application,'FieldWidth'),width:list_properties?.width?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("width"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'height',headerName:getLocale(application,'FieldHeight'),width:list_properties?.height?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("height"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'length',headerName:getLocale(application,'FieldLength'),width:list_properties?.length?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("length"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'weight',headerName:getLocale(application,'FieldWeight'),width:list_properties?.weight?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("weight"),type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'sku_type',headerName:getLocale(application,'FieldSKUType'),width:list_properties?.sku_type?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("sku_type")},
            {field:'parent_name',headerName:getLocale(application,'FieldParentPackageType'),width:list_properties?.parent_name?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("parent_name")},
            {field:'product_creation_default',headerName:getLocale(application,'FieldProductCreationDefault'),width:list_properties?.product_creation_default?.width??200,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("product_creation_default"),type:'boolean'},
        ]);
    },
    package_type_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=><LinkMUI component="button" variant="body2" 
                    onClick={()=>handleClickEvent(
                        {action:'selectRow',id:params.row.id,pickMap:{
                            type:params.row.type,
                            unit:params.row.unit,
                            amount:params.row.amount,
                            width:params.row.width,
                            height:params.row.height,
                            length:params.row.length,
                            weight:params.row.weight,
                        }})}>{params.row.name}</LinkMUI>},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??150,editable:false},
            {field:'unit',headerName:getLocale(application,'FieldUnit'),width:list_properties?.unit?.width??150,editable:false},
            {field:'amount',headerName:getLocale(application,'FieldAmount'),width:list_properties?.amount?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'width',headerName:getLocale(application,'FieldWidth'),width:list_properties?.width?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'height',headerName:getLocale(application,'FieldHeight'),width:list_properties?.height?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'length',headerName:getLocale(application,'FieldLength'),width:list_properties?.length?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'weight',headerName:getLocale(application,'FieldWeight'),width:list_properties?.weight?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    sku_stock_balance:function(list_properties,application,){
        return([
            {field:'amount',headerName:getLocale(application,'FieldAmount'),width:list_properties?.amount?.width??120,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'quarantined',headerName:getLocale(application,'FieldQuarantined'),width:list_properties?.quarantined?.width??120,editable:false,type:'boolean',
                renderCell:(params)=>BooleanWarning(params),
            },
            {field:'expiryDate',headerName:getLocale(application,'FieldExpiryDate'),width:list_properties?.expiryDate?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),
            },
            {field:'batchCode',headerName:getLocale(application,'PageTabBatch'),width:list_properties?.batchCode?.width??200,editable:false},
        ]);
    },
    product_attachments:function(list_properties,application,handleClickEvent,entityRules){
        const editable=(field)=>entityRules?.can_update&&(entityRules?.can_update_list_fields?.includes(field)??false);
        const documentTypeOptions=application?.globalData?.list_of_values?.Attachment_Document_Type||{};
        const cols=[
            {field:'filename',headerName:getLocale(application,'FieldName'),width:list_properties?.filename?.width??250,editable:false},
            {field:'document_type',headerName:getLocale(application,'FieldDocumentType'),width:list_properties?.document_type?.width??180,editable:false,
                valueGetter:(value)=>documentTypeOptions[value]||value},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??250,editable:editable('description')},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??170,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
            {field:'download',headerName:'',width:60,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(
                    <Tooltip title={application.getLocale("ButtonDownload")}>
                        <IconButton size="small" onClick={()=>handleClickEvent({action:'download',id:params.row.id,filename:params.row.filename})}>
                            <DownloadIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                ),
            },
        ];
        return cols.map(withEditableIcon);
    },
    batch_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'batch_number',headerName:getLocale(application,'FieldNumber'),width:list_properties?.batch_number?.width??110,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{batch_number:params.row.batch_number}})}>{params.row.batch_number}</LinkMUI>},
            {field:'tank_name',headerName:getLocale(application,'FieldTankName'),width:list_properties?.tank_name?.width??150,editable:false},
            {field:'product_name',headerName:getLocale(application,'FieldProductName'),width:list_properties?.product_name?.width??200,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false},
            {field:'start_date',headerName:getLocale(application,'FieldStartDate'),width:list_properties?.start_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'end_date',headerName:getLocale(application,'FieldEndDate'),width:list_properties?.end_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
        ]);
    },
    manufacturing_orders:function(list_properties,application,handleLinkClick){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/manufacturing_order/'+sanitizeId(params.row.id)+'/audit'} onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/batch/'+sanitizeId(params.row.batch_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.batch_number}</ThemedLink>
            },
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??220,editable:false,
                renderCell:(params)=><ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:false,
                renderCell:(params)=><NotesCell text={params.row.description} application={application}/>
            },
            {field:'planned_quantity',headerName:getLocale(application,'FieldPlannedQuantity'),width:list_properties?.planned_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'produced_quantity',headerName:getLocale(application,'FieldProducedQuantity'),width:list_properties?.produced_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderManufacturingOrderStatusCell(application,'status'),},
            {field:'due_date',headerName:getLocale(application,'FieldDueDate'),width:list_properties?.due_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'produced_at',headerName:getLocale(application,'FieldProducedAt'),width:list_properties?.produced_at?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
        ]);
    },
    batch_manufacturing_orders:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??150,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(params?.row?.can_update_row?<Button name="update" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>:<span>-</span>),},
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/manufacturing_order/'+sanitizeId(params.row.id)+'/audit'} openNewTab onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/batch/'+sanitizeId(params.row.batch_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.batch_number}</ThemedLink>
            },
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??220,editable:false,
                renderCell:(params)=><ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:false,
                renderCell:(params)=><NotesCell text={params.row.description} application={application}/>
            },
            {field:'planned_quantity',headerName:getLocale(application,'FieldPlannedQuantity'),width:list_properties?.planned_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'produced_quantity',headerName:getLocale(application,'FieldProducedQuantity'),width:list_properties?.produced_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderManufacturingOrderStatusCell(application,'status'),},
            {field:'due_date',headerName:getLocale(application,'FieldDueDate'),width:list_properties?.due_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'produced_at',headerName:getLocale(application,'FieldProducedAt'),width:list_properties?.produced_at?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
        ]);
    },
    // Same columns as the SKU tab's list - reused rather than duplicated a 4th time.
    product_manufacturing_orders:function(list_properties,application,handleLinkClick){
        return columns.sku_manufacturing_orders(list_properties,application,handleLinkClick);
    },
    sku_manufacturing_orders:function(list_properties,application,handleLinkClick){
        return([
            {field:'reference',headerName:getLocale(application,'FieldReference'),width:list_properties?.reference?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/manufacturing_order/'+sanitizeId(params.row.id)+'/audit'} onClick={handleLinkClick}>{params.row.reference}</ThemedLink>
            },
            {field:'batch_number',headerName:getLocale(application,'FieldBatchNumber'),width:list_properties?.batch_number?.width??110,editable:false,
                renderCell:(params)=><ThemedLink to={'/batch/'+sanitizeId(params.row.batch_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.batch_number}</ThemedLink>
            },
            {field:'sku_name',headerName:getLocale(application,'FieldSKUName'),width:list_properties?.sku_name?.width??220,editable:false,
                renderCell:(params)=><ThemedLink to={'/sku/'+sanitizeId(params.row.sku_id)+'/manufacturing_orders'} openNewTab onClick={handleLinkClick}>{params.row.sku_name}</ThemedLink>
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:false,
                renderCell:(params)=><NotesCell text={params.row.description} application={application}/>
            },
            {field:'planned_quantity',headerName:getLocale(application,'FieldPlannedQuantity'),width:list_properties?.planned_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'produced_quantity',headerName:getLocale(application,'FieldProducedQuantity'),width:list_properties?.produced_quantity?.width??150,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:renderManufacturingOrderStatusCell(application,'status'),},
            {field:'due_date',headerName:getLocale(application,'FieldDueDate'),width:list_properties?.due_date?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
            {field:'produced_at',headerName:getLocale(application,'FieldProducedAt'),width:list_properties?.produced_at?.width??110,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"date"),},
        ]);
    },
};

export const listPreDefaultFilter=(globalData)=>{
    return {
        accounts:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Account_Status.ACTIVE,
                        globalData?.list_of_values?.Account_Status.LOGISTICS_INTEGRATION_ERROR,
                        globalData?.list_of_values?.Account_Status.INVOICING_INTEGRATION_ERROR                    ]
                },
            ],
        },
        products:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Product_Status.ACTIVE,
                        globalData?.list_of_values?.Sku_Status.LOGISTICS_INTEGRATION_ERROR                    ],
                },
            ],
        },
        skus:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Sku_Status.ACTIVE,
                        globalData?.list_of_values?.Sku_Status.LOGISTICS_INTEGRATION_ERROR                    ]
                },
                // Community DataGrid applies only the first filter item, so only one default filter can live here (Sellable is a column users can filter manually).
            ],
        },
        sku_sales_order_items:{
            items:[
                {
                    field:'order_status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Sales_Order_Status.SUBMITTED,
                        globalData?.list_of_values?.Sales_Order_Status.DRAFT,
                        globalData?.list_of_values?.Sales_Order_Status.INVOICING_INTEGRATION_ERROR,
                        globalData?.list_of_values?.Sales_Order_Status.LOGISTICS_INTEGRATION_ERROR,
                    ],
                },
            ],
        },
        sales_orders:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Sales_Order_Status?.SUBMITTED,
                        globalData?.list_of_values?.Sales_Order_Status?.DRAFT,
                        globalData?.list_of_values?.Sales_Order_Status?.INVOICING_INTEGRATION_ERROR,
                        globalData?.list_of_values?.Sales_Order_Status?.LOGISTICS_INTEGRATION_ERROR,
                        globalData?.list_of_values?.Sales_Order_Status?.INVOICED,
                        globalData?.list_of_values?.Sales_Order_Status?.OVERDUE,
                        globalData?.list_of_values?.Sales_Order_Status?.PAYMENT_REMINDER_SENT,
                        globalData?.list_of_values?.Sales_Order_Status?.ON_DEBT_COLLECTION,
                        globalData?.list_of_values?.Sales_Order_Status?.PAID,
                        globalData?.list_of_values?.Sales_Order_Status?.INVALIDATED,
                        globalData?.list_of_values?.Sales_Order_Status?.NO_INVOICING,
                        globalData?.list_of_values?.Sales_Order_Status?.NOT_FOUND,
                    ],
                },
            ],
        },
        // Hide cancelled MOs by default so they aren't mistaken for active ones.
        manufacturing_orders:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Manufacturing_Order_Status?.DRAFT,
                        globalData?.list_of_values?.Manufacturing_Order_Status?.IN_PROGRESS,
                        globalData?.list_of_values?.Manufacturing_Order_Status?.PRODUCED,
                    ],
                },
            ],
        },
    }
}

export const listPreDefaultSorting={
    //No Pre Defaults So Far!
    /*
    accounts:[{
        field:'name',
        sort:'asc',
    }],
    */
}

export const getAccountListBehavior=(application)=>{
    const statusLov=application?.globalData?.list_of_values_reverse?.Account_Status;
    const errorStatuses=["LOGISTICS_INTEGRATION_ERROR","INVOICING_INTEGRATION_ERROR"];
    const getRowClassName=(params)=>{
        const rawStatus=params?.row?.status;
        const translatedStatus=rawStatus&&statusLov?.[rawStatus];
        return errorStatuses.includes(translatedStatus)?"highlight-row":"";
    };
    const dataGridSx={
        "& .highlight-row":{
            backgroundColor:"#ffe6f0 !important",
            color:"black",
        },
    };
    return {
        getRowClassName,
        dataGridSx,
    };
};

export const getStockBalanceListBehavior=(application)=>{
    const getRowClassName=(params)=>{
        const highlightRow=params?.row?.quarantined;
        return highlightRow?"highlight-row":"";
    };
    const dataGridSx={
        "& .highlight-row":{
            backgroundColor:"#ffe6f0 !important",
            color:"black",
        },
    };
    return {
        getRowClassName,
        dataGridSx,
    };
};

export const getProductListBehavior=(application)=>{
    const statusLov=application?.globalData?.list_of_values_reverse?.Product_Status;
    const errorStatuses=["LOGISTICS_INTEGRATION_ERROR","INVOICING_INTEGRATION_ERROR"];
    const getRowClassName=(params)=>{
        const rawStatus=params?.row?.status;
        const translatedStatus=rawStatus&&statusLov?.[rawStatus];
        return errorStatuses.includes(translatedStatus)?"highlight-row":"";
    };
    const dataGridSx={
        "& .highlight-row":{
            backgroundColor:"#ffe6f0 !important",
            color:"black",
        },
    };
    return {
        getRowClassName,
        dataGridSx,
    };
};
